import { Analyse } from "../state/schema";
import { parseAmount } from "./format";

/* Haushaltsrechnung (§20). Identische Logik wird später serverseitig
   für PDF/E-Mail wiederverwendet. */

export interface Haushalt {
  nettoMonat: number;
  zusatzgehaelterMonat: number;
  nebeneinkuenfte: number;
  kindergeld: number;
  weitereEinnahmen: number;
  einnahmenGesamt: number;

  laufendeAusgaben: number;
  ausgabenPosten: { label: string; wert: number }[];

  sparbeitraege: number;
  laufendeVerbindlichkeiten: number;
  vorSparenVerfuegbar: number;
  freiVerfuegbar: number;
}

/** Summe der je Vermögens-Posten erfassten monatlichen Sparbeiträge. */
export function summeSparbeitraege(a: Analyse): number {
  return a.vermoegen.posten.reduce((s, p) => s + parseAmount(p.sparbeitrag), 0);
}

/** Summe der oben erfassten Versicherungs-/Vorsorgebeiträge (AV + AKS + Sach),
 *  alles auf den Monat umgerechnet. „Beitrag unbekannt" zählt als 0. */
export function summeVersicherungsbeitraege(a: Analyse): number {
  let s = 0;
  for (const v of a.altersvorsorge.vertraege) if (!v.beitragUnbekannt) s += parseAmount(v.beitrag);
  for (const v of a.arbeitskraft.vertraege) if (!v.beitragUnbekannt) s += parseAmount(v.beitrag);
  for (const v of a.sachversicherungen.vertraege) {
    const b = parseAmount(v.beitrag);
    s += v.intervall === "anual" ? b / 12 : b; // mensual = estándar
  }
  return s;
}

export function gehaltszahlungenProJahr(a: Analyse): number {
  const sel = a.einkommen.gehaltszahlungen;
  if (sel === "otra") {
    const n = Math.round(parseAmount(a.einkommen.gehaltszahlungenAndere));
    return n >= 1 ? n : 12;
  }
  const n = parseInt(sel, 10);
  return isFinite(n) && n >= 1 ? n : 12;
}

export function berechneHaushalt(a: Analyse): Haushalt {
  const netto = parseAmount(a.einkommen.nettoMonat);
  const zahlungen = gehaltszahlungenProJahr(a);
  // Zusätzliche Gehaltszahlungen auf Monatsdurchschnitt umrechnen
  const zusatz = netto * (Math.max(zahlungen - 12, 0) / 12);

  const nebeneinkuenfte = parseAmount(a.einkommen.nebeneinkuenfte);
  const kindergeld = parseAmount(a.einkommen.kindergeld);
  const weitere = parseAmount(a.einkommen.weitereEinnahmen);

  // Brutto wird bewusst NICHT addiert (nur Info, §17/§20)
  const einnahmenGesamt = netto + zusatz + nebeneinkuenfte + kindergeld + weitere;

  const g = a.ausgaben;
  const ausgabenPosten = [
    { label: "Costos de vivienda (con gastos)", wert: parseAmount(g.wohnen) },
    { label: "Gastos de vida diaria", wert: parseAmount(g.lebenshaltung) },
    { label: "Movilidad", wert: parseAmount(g.mobilitaet) },
    { label: "Cuotas de crédito", wert: parseAmount(g.kreditraten) },
    { label: "Pensión alimenticia", wert: parseAmount(g.unterhalt) },
    { label: "Seguros", wert: parseAmount(g.versicherungen) },
    { label: "Suscripciones y asociaciones", wert: parseAmount(g.abosVereine) },
    { label: "Otros gastos", wert: parseAmount(g.sonstige) },
  ];
  const laufendeAusgaben = ausgabenPosten.reduce((s, p) => s + p.wert, 0);

  const sparbeitraege = parseAmount(g.sparbeitraege);
  const laufendeVerbindlichkeiten = laufendeAusgaben + sparbeitraege;
  const vorSparenVerfuegbar = einnahmenGesamt - laufendeAusgaben;
  const freiVerfuegbar = vorSparenVerfuegbar - sparbeitraege;

  return {
    nettoMonat: netto,
    zusatzgehaelterMonat: zusatz,
    nebeneinkuenfte,
    kindergeld,
    weitereEinnahmen: weitere,
    einnahmenGesamt,
    laufendeAusgaben,
    ausgabenPosten,
    sparbeitraege,
    laufendeVerbindlichkeiten,
    vorSparenVerfuegbar,
    freiVerfuegbar,
  };
}
